import { useMemo } from 'react';
import { ITestResult } from '../../models/testResult';
import { ITestDefinition } from '../../models/testDefinition';
import { Athlete, TimeframeKey } from '../components/testingTypes';
import { calculatePercentile } from '../../utils/testing-calculations';

/** Punto nello scatter cross-test: un atleta con valore su entrambi gli assi */
export interface CrossTestPoint {
  athleteId: string;
  athleteName: string;
  x: number;
  y: number;
}

/** Risultato selettore cross-test con correlazione Pearson (sez.8.3) */
export interface CrossTestData {
  points: CrossTestPoint[];
  /** Pearson r, null se < 3 punti */
  pearsonR: number | null;
}

export interface PercentileCell {
  athleteId: string;
  testId: string;
  latestValue: number | null;
  percentile: number | null;
  history: { date: string; value: number }[];
}

export interface BoxPlotStats {
  testId: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: number[];
}

export interface RoleSeriesPoint {
  date: string;
  [role: string]: number | string;
}

/**
 * Percentile con DIREZIONE DI MERITO (spec Testing sez.8.2): per i test `lower_better`
 * (tempi, tempo di contatto…) un valore più BASSO è migliore → percentile invertito.
 *
 * DELEGA a @leopart/calc (2026-07-28): semantica canonica backend — metodo
 * midpoint sui ties e conteggio dei "peggiori" direction-aware (per
 * lower_better conta gli strettamente maggiori, non 100-pct). Prima qui
 * viveva una terza variante di percentile (rank strict, senza ties): drift
 * R7 ritirato. n=1 → 100; il caso array vuoto (→0) non è raggiungibile dal
 * call-site (percentile calcolato solo se latestValue è nei teamValues).
 */
function computePercentile(
  value: number,
  allValues: number[],
  lowerBetter = false,
): number {
  return calculatePercentile(
    value,
    allValues,
    lowerBetter ? 'lower_better' : 'higher_better',
  );
}

function computeBoxStats(values: number[]): Omit<BoxPlotStats, 'testId'> | null {
  if (values.length < 2) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const q1 = sorted[Math.floor(n * 0.25)];
  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];
  const q3 = sorted[Math.floor(n * 0.75)];
  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;
  const nonOutliers = sorted.filter((v) => v >= lowerFence && v <= upperFence);
  const outliers = sorted.filter((v) => v < lowerFence || v > upperFence);
  return {
    min: nonOutliers.length > 0 ? nonOutliers[0] : sorted[0],
    q1,
    median,
    q3,
    max: nonOutliers.length > 0 ? nonOutliers[nonOutliers.length - 1] : sorted[n - 1],
    outliers,
  };
}

function getStartDate(timeframe: TimeframeKey): Date {
  const today = new Date();
  switch (timeframe) {
    case '1m': { const d = new Date(today); d.setMonth(d.getMonth() - 1); return d; }
    case '3m': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return d; }
    case '6m': { const d = new Date(today); d.setMonth(d.getMonth() - 6); return d; }
    case 'season': {
      const d = new Date(today.getFullYear(), 8, 1);
      if (today.getMonth() < 8) d.setFullYear(today.getFullYear() - 1);
      return d;
    }
    case 'all':
    default:
      return new Date('2020-01-01');
  }
}

/**
 * Calcolo correlazione di Pearson (sez.8.3).
 * Restituisce null se n < 3 o deviazione standard = 0.
 */
function pearsonR(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 3) return null;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, dX = 0, dY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    dX += dx * dx;
    dY += dy * dy;
  }
  if (dX === 0 || dY === 0) return null;
  return Math.round((num / Math.sqrt(dX * dY)) * 100) / 100;
}

export function useTeamAnalytics(
  categoryTests: ITestDefinition[],
  athletes: Athlete[],
  historyMap: Record<string, ITestResult[]>,
  timeframe: TimeframeKey,
  selectedRoles: string[],
) {
  const startDateStr = useMemo(() => getStartDate(timeframe).toISOString().split('T')[0], [timeframe]);
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Latest value per athlete per test (within timeframe)
  const latestValueMap = useMemo(() => {
    const map: Record<string, number> = {};
    athletes.forEach((athlete) => {
      categoryTests.forEach((test) => {
        const history = historyMap[`${athlete.id}_${test.id}`] ?? [];
        const filtered = history.filter(
          (r) => r.value > 0 && r.date >= startDateStr && r.date <= todayStr,
        );
        if (filtered.length > 0) {
          const latest = filtered.reduce((a, b) =>
            new Date(a.date) > new Date(b.date) ? a : b,
          );
          map[`${athlete.id}_${test.id}`] = latest.value;
        }
      });
    });
    return map;
  }, [athletes, categoryTests, historyMap, startDateStr, todayStr]);

  // Per-test sorted values for percentile computation
  const testValueLists = useMemo(() => {
    const map: Record<string, number[]> = {};
    categoryTests.forEach((test) => {
      map[test.id] = athletes
        .map((a) => latestValueMap[`${a.id}_${test.id}`])
        .filter((v): v is number => v !== undefined);
    });
    return map;
  }, [categoryTests, athletes, latestValueMap]);

  // Heatmap cells
  const percentileCells = useMemo((): PercentileCell[] => {
    const cells: PercentileCell[] = [];
    athletes.forEach((athlete) => {
      categoryTests.forEach((test) => {
        const history = (historyMap[`${athlete.id}_${test.id}`] ?? [])
          .filter((r) => r.value > 0 && r.date >= startDateStr && r.date <= todayStr)
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const latestValue = latestValueMap[`${athlete.id}_${test.id}`] ?? null;
        const teamValues = testValueLists[test.id] ?? [];
        const percentile =
          latestValue !== null
            ? computePercentile(
                latestValue,
                teamValues,
                test.direction === 'lower_better',
              )
            : null;

        cells.push({
          athleteId: athlete.id,
          testId: test.id,
          latestValue,
          percentile,
          history: history.map((r) => ({ date: r.date, value: r.value })),
        });
      });
    });
    return cells;
  }, [athletes, categoryTests, historyMap, latestValueMap, testValueLists, startDateStr, todayStr]);

  // Box plot stats per test
  const boxPlotStats = useMemo((): BoxPlotStats[] => {
    return categoryTests
      .map((test) => {
        const values = testValueLists[test.id] ?? [];
        const stats = computeBoxStats(values);
        if (!stats) return null;
        return { testId: test.id, ...stats };
      })
      .filter((s): s is BoxPlotStats => s !== null);
  }, [categoryTests, testValueLists]);

  // Role comparison — group athletes by position, compute monthly average
  const allRoles = useMemo(() => {
    const roles = new Set<string>();
    athletes.forEach((a) => {
      if (a.position) roles.add(a.position);
    });
    return Array.from(roles).sort();
  }, [athletes]);

  const activeRoles = useMemo(
    () => (selectedRoles.length > 0 ? selectedRoles : allRoles),
    [selectedRoles, allRoles],
  );

  const roleSeriesMap = useMemo((): Record<string, RoleSeriesPoint[]> => {
    const result: Record<string, RoleSeriesPoint[]> = {};
    categoryTests.forEach((test) => {
      const monthlyData: Record<string, Record<string, number[]>> = {};

      athletes.forEach((athlete) => {
        const role = athlete.position ?? 'Altro';
        if (!activeRoles.includes(role)) return;

        const history = (historyMap[`${athlete.id}_${test.id}`] ?? []).filter(
          (r) => r.value > 0 && r.date >= startDateStr && r.date <= todayStr,
        );

        history.forEach((r) => {
          const monthKey = r.date.slice(0, 7); // YYYY-MM
          if (!monthlyData[monthKey]) monthlyData[monthKey] = {};
          if (!monthlyData[monthKey][role]) monthlyData[monthKey][role] = [];
          monthlyData[monthKey][role].push(r.value);
        });
      });

      const sortedMonths = Object.keys(monthlyData).sort();
      result[test.id] = sortedMonths.map((month) => {
        const point: RoleSeriesPoint = { date: month };
        activeRoles.forEach((role) => {
          const vals = monthlyData[month]?.[role] ?? [];
          if (vals.length > 0) {
            point[role] = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
          }
        });
        return point;
      });
    });
    return result;
  }, [categoryTests, athletes, historyMap, activeRoles, startDateStr, todayStr]);

  return {
    percentileCells,
    boxPlotStats,
    roleSeriesMap,
    allRoles,
    activeRoles,
  };
}

/**
 * Selettore coppie (x,y) per atleta dati due testId (spec sez.8.3 — relazioni cross-test).
 * Restituisce solo gli atleti che hanno entrambi i valori nel timeframe corrente.
 * Usa i latestValue già calcolati dal hook principale via historyMap.
 */
export function useCrossTestScatter(
  athletes: Athlete[],
  historyMap: Record<string, ITestResult[]>,
  timeframe: TimeframeKey,
  testIdX: string | null,
  testIdY: string | null,
): CrossTestData {
  const startDateStr = useMemo(() => {
    const today = new Date();
    switch (timeframe) {
      case '1m': { const d = new Date(today); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; }
      case '3m': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return d.toISOString().split('T')[0]; }
      case '6m': { const d = new Date(today); d.setMonth(d.getMonth() - 6); return d.toISOString().split('T')[0]; }
      case 'season': {
        const d = new Date(today.getFullYear(), 8, 1);
        if (today.getMonth() < 8) d.setFullYear(today.getFullYear() - 1);
        return d.toISOString().split('T')[0];
      }
      default: return '2020-01-01';
    }
  }, [timeframe]);

  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);

  return useMemo((): CrossTestData => {
    if (!testIdX || !testIdY || testIdX === testIdY) {
      return { points: [], pearsonR: null };
    }

    const points: CrossTestPoint[] = [];

    athletes.forEach((athlete) => {
      const histX = (historyMap[`${athlete.id}_${testIdX}`] ?? [])
        .filter((r) => r.value > 0 && r.date >= startDateStr && r.date <= todayStr);
      const histY = (historyMap[`${athlete.id}_${testIdY}`] ?? [])
        .filter((r) => r.value > 0 && r.date >= startDateStr && r.date <= todayStr);

      if (histX.length === 0 || histY.length === 0) return;

      const latestX = histX.reduce((a, b) => new Date(a.date) > new Date(b.date) ? a : b);
      const latestY = histY.reduce((a, b) => new Date(a.date) > new Date(b.date) ? a : b);

      points.push({ athleteId: athlete.id, athleteName: athlete.name, x: latestX.value, y: latestY.value });
    });

    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);

    return { points, pearsonR: pearsonR(xs, ys) };
  }, [athletes, historyMap, testIdX, testIdY, startDateStr, todayStr]);
}
